// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getGoogleAnalyticsConnectorMapState } from "@sc/App/GoogleAnalyticsConnector/Store/Selectors/GoogleAnalyticsSelectors";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import Button, { ButtonPresentationProps } from "@sc/MobiusOverrides/Button/Button";
import LazyImage from "@sc/MobiusOverrides/LazyImage";
import parse from "html-react-parser";
import * as React from "react";
import { shallowEqual } from "react-redux";

interface OwnProps extends WidgetProps {
    fields: {
        buttonText: string;
        title: string;
        description: string;
        url: LinkFieldValue;
        imagePath: string;
        openInNewTab: boolean;
        buttonVariant?: "primary" | "secondary" | "link";
        trackIndustriesServedEvent?: boolean;
    };
}

type Props = OwnProps;

export interface InfoCardStyles {
    wrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    cardContentWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    button?: ButtonPresentationProps;
}

export const infoCardStyles: InfoCardStyles = {};

const InfoCard: React.FC<Props> = ({ fields }) => {
    // XNG-Change
    const { xGoogleAnalytics } = useAppSelector((state: ApplicationState) => {
        const gaState = getGoogleAnalyticsConnectorMapState(state);
        return {
            xGoogleAnalytics: gaState.xGoogleAnalytics,
        };
    }, shallowEqual);
    const styles = infoCardStyles;
    const { buttonText, title, description, url, imagePath, openInNewTab, buttonVariant, trackIndustriesServedEvent } =
        fields;

    const link = useGetLink(url);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const handleButtonOnClick = () => {
        if (!link?.url) {
            return;
        }
        // XNG-Changes
        if (trackIndustriesServedEvent && xGoogleAnalytics?.industriesServedEvent) {
            const dataLayer = getDataLayer();
            if (dataLayer) {
                const clickText = buttonText || title || "";
                const destination = link.url || "";

                const googleAnalyticsData = {
                    event: xGoogleAnalytics.industriesServedEventName || "industries_served",
                    click_text: clickText,
                    destination,
                    affiliation: xGoogleAnalytics.affiliation || "",
                    sales_rep: xGoogleAnalytics.salesRep || "",
                    is_rep: xGoogleAnalytics.isRep || "",
                    bill_to_num: xGoogleAnalytics.billToNum || "",
                    ship_to_num: xGoogleAnalytics.shipToNum || "",
                    cust_type: xGoogleAnalytics.customerType || "",
                    login_type: xGoogleAnalytics.loginType || "",
                    warehouse: xGoogleAnalytics.warehouse || "",
                };

                dataLayer.push({ ecommerce: null });
                dataLayer.push(googleAnalyticsData);
            }
        }

        if (openInNewTab) {
            window.open(link?.url, "_blank");
            return;
        }

        if (link?.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            history.push(link?.url);
        } else {
            history.push(link?.url);
        }
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={imagePath} {...styles.image} altText="Card Image" />
            </StyledWrapper>
            <StyledWrapper {...styles.cardContentWrapper}>
                <Typography {...styles.title}>{title}</Typography>
                <Typography {...styles.description}>
                    {typeof description === "string" ? parse(description, parserOptions) : description}
                </Typography>
                {buttonText && (
                    <>
                        {buttonVariant !== "link" ? (
                            <Button
                                {...styles.button}
                                variant={buttonVariant}
                                aria-label={buttonText}
                                onClick={handleButtonOnClick}
                            >
                                {buttonText}
                            </Button>
                        ) : (
                            <Link
                                {...styles.link}
                                aria-label={`${buttonText} ${link.title}`}
                                onClick={handleButtonOnClick}
                            >
                                {buttonText}
                            </Link>
                        )}
                    </>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCard,
    definition: {
        displayName: "Info Card",
        group: "Xngage" as any,
        isSystem: true,
        fieldDefinitions: [
            {
                name: "imagePath",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Image",
                fieldType: "General",
            },
            {
                name: "title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: "description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                extendedConfig: { height: 120 },
                displayName: "Description",
                fieldType: "General",
            },
            {
                name: "buttonText",
                editorTemplate: "TextField",
                defaultValue: "",
                displayName: "Button Label",
                fieldType: "General",
            },
            {
                name: "buttonVariant",
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Link", value: "link" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "General",
            },
            {
                name: "url",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                displayName: "Button URL",
                fieldType: "General",
            },
            {
                name: "openInNewTab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: "trackIndustriesServedEvent",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                displayName: "Track Industries Served Event",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
